import { useCallback, useEffect, useRef } from "react";
import { getSocket } from "../../utils/web-socket";

/**
 * Shared hook that fetches extraction progress from the API on mount
 * and re-fetches on WebSocket reconnection.
 *
 * Eliminates the duplicated hasFetchedProgress / hasConnected ref pattern
 * previously copy-pasted across ExtractProgress, Cad2ImageProgress, and
 * PCExtractProgress.
 *
 * @param {number|string|null} jobId
 * @param {Object} actions
 * @param {Function} actions.getJobProgress
 * @param {Function} actions.restoreCad2ImageProgress
 */
const useProgressRestoration = (
	jobId,
	{ getJobProgress, restoreCad2ImageProgress }
) => {
	// TEMP: progress restoration disabled. Re-enable by removing this early return
	// once the underlying restoration flow is ready to be turned back on.
	// eslint-disable-next-line no-unreachable, react-hooks/rules-of-hooks
	return;
	// eslint-disable-next-line no-unreachable
	const lastFetchedJobId = useRef(null);
	const hasConnected = useRef(false);

	const fetchAndRestore = useCallback(() => {
		if (jobId == null) return;
		getJobProgress({
			jobId,
			cb: (result) => {
				if (result && result.records) {
					restoreCad2ImageProgress(result);
				}
			},
		});
	}, [jobId, getJobProgress, restoreCad2ImageProgress]);

	// Fetch when jobId becomes available or changes
	useEffect(() => {
		if (jobId == null) return;
		const normalizedJobId = String(jobId);
		if (lastFetchedJobId.current === normalizedJobId) return;
		lastFetchedJobId.current = normalizedJobId;
		fetchAndRestore();
	}, [jobId, fetchAndRestore]);

	// Re-fetch on WebSocket reconnect (skip the initial connect event)
	useEffect(() => {
		if (jobId == null) return;

		const socket = getSocket();
		if (socket.connected) {
			hasConnected.current = true;
		}

		const handleConnect = () => {
			if (!hasConnected.current) {
				hasConnected.current = true;
				return;
			}
			fetchAndRestore();
		};

		socket.on("connect", handleConnect);

		return () => {
			socket.off("connect", handleConnect);
		};
	}, [jobId, fetchAndRestore]);
};

export default useProgressRestoration;
